import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { PointTransaction, PointsContextType } from "../types/points";
import { apiClient } from "../utils/API/apiClient";

const PointsContext = createContext<PointsContextType | undefined>(undefined);

export const PointsProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [points, setPoints] = useState<number>(0);
  const [transactions, setTransactions] = useState<PointTransaction[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const getAuthToken = () => localStorage.getItem("auth_token") || "";

  const refreshPoints = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const response = await apiClient<{
        points: number;
        transactions: PointTransaction[];
      }>("/api/Points/balance", {
        token: getAuthToken(),
        apiType: 2,
      });
      setPoints(response.data.points);
      setTransactions(response.data.transactions);
    } catch (error) {
      console.error("Error fetching points balance:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load from backend when user logs in
  useEffect(() => {
    if (user) {
      refreshPoints();
    } else {
      setPoints(0);
      setTransactions([]);
    }
  }, [user]);

  const addPoints = async (amount: number, description: string) => {
    if (!user) return;
    try {
      await apiClient<PointTransaction>("/api/Points/add", {
        method: "POST",
        token: getAuthToken(),
        apiType: 2,
        body: { amount, description },
      });
      await refreshPoints();
    } catch (error) {
      console.error("Error adding points:", error);
      throw error;
    }
  };

  const deductPoints = async (
    amount: number,
    description: string
  ): Promise<boolean> => {
    if (!user) return false;
    try {
      await apiClient<PointTransaction>("/api/Points/deduct", {
        method: "POST",
        token: getAuthToken(),
        apiType: 2,
        body: { amount, description },
      });
      await refreshPoints();
      return true;
    } catch (error) {
      console.error("Error deducting points:", error);
      return false;
    }
  };

  return (
    <PointsContext.Provider
      value={{ points, transactions, addPoints, deductPoints, refreshPoints }}
    >
      {children}
    </PointsContext.Provider>
  );
};

export const usePoints = () => {
  const context = useContext(PointsContext);
  if (!context) {
    throw new Error("usePoints must be used within a PointsProvider");
  }
  return context;
};
